import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import GroupMembers from './GroupMembers';
import GameMatch from './GameMatch';
import AddCourt from './AddCourt';
import CourtManager from './CourtManager';
import PlayerManager from './PlayerManager';
import useMobileViewportZoom from './useMobileViewportZoom';
import { API_BASE } from "./config";
import socket from "../socket/socket";
import GroupJoinQrModal from './GroupJoinQrModal';
import '../css/Dashboard.css';

const formatGroupDate = value => {
  if (!value) return 'ไม่ระบุวันที่';

  const datePart = String(value).slice(0, 10);
  const [year, month, day] = datePart.split('-');

  if (!year || !month || !day) return String(value);
  return `${day}/${month}/${year}`;
};

const AUTO_REFRESH_INTERVAL_SECONDS = 300;

const formatAutoRefreshCountdown = seconds => {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};

const MENU_MODAL_DETAILS = {
  groups: {
    eyebrow: 'GROUP CONTROL',
    title: '📁 จัดการ Groups',
    description: 'สร้าง เลือก และจัดการ Group โดยไม่ต้องเปลี่ยนหน้า'
  },
  members: {
    eyebrow: 'MEMBER CONTROL',
    title: '👥 จัดการสมาชิก',
    description: 'ตรวจสอบสมาชิกและ Check-out ผู้เล่นใน Group ที่เลือก'
  },
  court: {
    eyebrow: 'COURT CONTROL',
    title: '🏟 จัดการ Court',
    description: 'เพิ่ม ลบ Check-in และ Check-out ได้โดยไม่ต้องเปลี่ยนหน้า'
  },
  players: {
    eyebrow: 'PLAYER CONTROL',
    title: '🧑 จัดการผู้เล่น',
    description: 'ค้นหา แก้ไขสถานะ และจัดการข้อมูลผู้เล่น'
  }
};

function Dashboard() {
  useMobileViewportZoom();

  const navigate = useNavigate();
  const location = useLocation();

  const [menu, setMenu] = useState('groups');
  const [groups, setGroups] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [groupDate, setGroupDate] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [deletingGroupId, setDeletingGroupId] = useState(null);
  const [updatingGroupStatusId, setUpdatingGroupStatusId] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [qrGroup, setQrGroup] = useState(null);
  const [monitorQrGroup, setMonitorQrGroup] = useState(null);
  const [refreshingDashboard, setRefreshingDashboard] = useState(false);
  const [dashboardRefreshVersion, setDashboardRefreshVersion] = useState(0);
  const [autoRefreshSeconds, setAutoRefreshSeconds] = useState(AUTO_REFRESH_INTERVAL_SECONDS);
  // 🔥 realtime state
  const [courts, setCourts] = useState([]);

  const user = useMemo(
    () => JSON.parse(localStorage.getItem('user')),
    []
  );
  const dashboardAction = location.state?.dashboardAction;
  const dashboardGroupId = location.state?.selectedGroupId;

  /* ===============================
     AUTH CHECK
  =============================== */
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!activeModal) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setActiveModal(null);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeModal]);

  /* ===============================
     LOAD GROUPS
  =============================== */
  useEffect(() => {
    fetchGroups();
  }, []);

  /* ===============================
     REALTIME GROUP DELETE
  =============================== */
  useEffect(() => {
    const handleGroupDeleted = data => {
      const deletedGroupId = Number(data.groupId);

      setGroups(currentGroups =>
        currentGroups.filter(group => Number(group.id) !== deletedGroupId)
      );

      setSelectedGroupId(currentGroupId => {
        if (Number(currentGroupId) === deletedGroupId) {
          setMenu('groups');
          setActiveModal(null);
          return null;
        }

        return currentGroupId;
      });
    };

    const handleGroupStatusUpdated = data => {
      const changedGroupId = Number(data.groupId ?? data.group_id);
      const nextStatus = data.Group_Status || data.status;
      if (!changedGroupId || !nextStatus) return;

      setGroups(currentGroups => currentGroups.map(group =>
        Number(group.id) === changedGroupId
          ? { ...group, Group_Status: nextStatus }
          : group
      ));
    };

    socket.on('group-deleted', handleGroupDeleted);
    socket.on('group-closed', handleGroupStatusUpdated);
    socket.on('group-opened', handleGroupStatusUpdated);
    socket.on('member-joined', fetchGroups);
    socket.on('member-updated', fetchGroups);
    socket.on('member-checkout', fetchGroups);
    socket.on('player-status-updated', fetchGroups);

    return () => {
      socket.off('group-deleted', handleGroupDeleted);
      socket.off('group-closed', handleGroupStatusUpdated);
      socket.off('group-opened', handleGroupStatusUpdated);
      socket.off('member-joined', fetchGroups);
      socket.off('member-updated', fetchGroups);
      socket.off('member-checkout', fetchGroups);
      socket.off('player-status-updated', fetchGroups);
    };
  }, []);

  const fetchGroups = async () => {
    const res = await fetch(`${API_BASE}/api/groups`);
    const data = await res.json();
    const loadedGroups = Array.isArray(data) ? data : [];
    const groupsWithRealMemberCount = await Promise.all(
      loadedGroups.map(async group => {
        try {
          const membersResponse = await fetch(
            `${API_BASE}/api/groups/${Number(group.id)}/members`
          );
          if (!membersResponse.ok) throw new Error('โหลดสมาชิกล้มเหลว');
          const members = await membersResponse.json();
          const uniqueUserIds = new Set(
            (Array.isArray(members) ? members : [])
              .map(member => Number(member.user_id))
              .filter(Number.isInteger)
          );
          return { ...group, total_members: uniqueUserIds.size };
        } catch (memberCountError) {
          console.error(`LOAD GROUP ${group.id} MEMBER COUNT ERROR:`, memberCountError);
          return { ...group, total_members: Number(group.total_members || 0) };
        }
      })
    );
    setGroups(groupsWithRealMemberCount);
  };

  const openGroupRegisterPage = group => {
    navigate(`/register?groupId=${encodeURIComponent(group.id)}`);
  };
  /* ===============================
     LOAD COURTS (Realtime Base)
  =============================== */
  const fetchCourts = async (groupId) => {
    if (!groupId) return;

    const res = await fetch(`${API_BASE}/api/courts/${groupId}`);
    const data = await res.json();
    setCourts(data);
  };

  const refreshDashboard = async () => {
    if (refreshingDashboard) return;

    try {
      setRefreshingDashboard(true);
      await Promise.all([
        fetchGroups(),
        selectedGroupId ? fetchCourts(selectedGroupId) : Promise.resolve()
      ]);
      setDashboardRefreshVersion(version => version + 1);
      setAutoRefreshSeconds(AUTO_REFRESH_INTERVAL_SECONDS);
    } catch (error) {
      console.error('REFRESH DASHBOARD ERROR:', error);
    } finally {
      setRefreshingDashboard(false);
    }
  };

  // Refresh the dashboard periodically so player status stays in sync after
  // a Match is created or started from another Dashboard/monitor session.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setAutoRefreshSeconds(current => {
        if (current <= 1) {
          refreshDashboard();
          return AUTO_REFRESH_INTERVAL_SECONDS;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [selectedGroupId]);

  /* ===============================
     SOCKET LISTENER (สำคัญมาก)
  =============================== */
 useEffect(() => {

  if (!selectedGroupId) return;

  console.log("📡 Dashboard join-group:", selectedGroupId);

  socket.emit("join-group", selectedGroupId);

  fetchCourts(selectedGroupId);

  const handleCourtUpdate = (data) => {
    console.log("🔥 Court Event:", data);

    if (Number(data.groupId) === Number(selectedGroupId)) {
      fetchCourts(selectedGroupId);
    }
  };

  /* ===== Court Events ===== */
  socket.on("courts-added", handleCourtUpdate);
  socket.on("court-status-updated", handleCourtUpdate);
  socket.on("court-deleted", handleCourtUpdate);
  socket.on("courts-initialized", handleCourtUpdate);

  return () => {

    socket.off("courts-added", handleCourtUpdate);
    socket.off("court-status-updated", handleCourtUpdate);
    socket.off("court-deleted", handleCourtUpdate);
    socket.off("courts-initialized", handleCourtUpdate);

  };

}, [selectedGroupId]);

  /* ===============================
     CREATE GROUP
  =============================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const res = await fetch(`${API_BASE}/api/groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        Group_Name: groupName,
        Group_Date: groupDate
      })
    });

    const data = await res.json();
    if (!res.ok) return alert(data.message);

    setGroupName('');
    setGroupDate('');
    fetchGroups();
  };

  /* ===============================
     DELETE GROUP
  =============================== */
  const handleDeleteGroup = async group => {
    if (deletingGroupId !== null) return;

    const confirmed = window.confirm(
      `ต้องการลบ Group "${group.Group_Name}" ใช่หรือไม่?\n\n` +
      'สมาชิกและสนามที่อยู่ใน Group นี้จะถูกนำออกด้วย และไม่สามารถย้อนกลับได้'
    );

    if (!confirmed) return;

    try {
      setDeletingGroupId(group.id);

      const res = await fetch(`${API_BASE}/api/groups/${group.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || 'ไม่สามารถลบ Group ได้');
      }

      setGroups(currentGroups =>
        currentGroups.filter(item => Number(item.id) !== Number(group.id))
      );

      if (Number(selectedGroupId) === Number(group.id)) {
        setSelectedGroupId(null);
        setMenu('groups');
        setActiveModal(null);
      }

      alert(data.message || 'ลบ Group สำเร็จ');
    } catch (error) {
      alert(`❌ ${error.message}`);
    } finally {
      setDeletingGroupId(null);
    }
  };

  const handleToggleGroupStatus = async group => {
    if (updatingGroupStatusId !== null) return;

    const isClosed = ['CLOSE', 'CLOSED'].includes(
      String(group.Group_Status || '').toUpperCase()
    );
    const nextStatus = isClosed ? 'OPEN' : 'CLOSED';
    const actionLabel = isClosed ? 'เปิด' : 'ปิด';

    if (!window.confirm(`${actionLabel} Group "${group.Group_Name}" ใช่หรือไม่?`)) return;

    try {
      setUpdatingGroupStatusId(group.id);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/groups/${group.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ status: nextStatus })
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(data.message || `ไม่สามารถ${actionLabel} Group ได้`);

      setGroups(currentGroups => currentGroups.map(item =>
        Number(item.id) === Number(group.id)
          ? { ...item, Group_Status: data.Group_Status || nextStatus }
          : item
      ));
      alert(data.message || `${actionLabel} Group สำเร็จ`);
    } catch (error) {
      alert(`❌ ${error.message}`);
    } finally {
      setUpdatingGroupStatusId(null);
    }
  };

  const logout = () => {
    localStorage.clear();
    navigate('/');
  };

  const openMenuModal = (modalName, groupId) => {
    const targetGroupId = groupId || selectedGroupId;
    const needsGroup = ['members', 'court'].includes(modalName);

    if (!MENU_MODAL_DETAILS[modalName]) return;

    if (needsGroup && !targetGroupId) {
      alert('กรุณาเลือก Group ก่อนเปิดเมนูนี้');
      return;
    }

    if (
      targetGroupId &&
      Number(targetGroupId) !== Number(selectedGroupId)
    ) {
      setSelectedGroupId(targetGroupId);
    }

    if (modalName === 'court') {
      fetchCourts(targetGroupId);
    }

    setActiveModal(modalName);
  };

  const openMatchPage = groupId => {
    const targetGroupId = groupId || selectedGroupId;

    if (!targetGroupId) {
      alert('กรุณาเลือก Group ก่อนเปิดหน้าจัด Match');
      return;
    }

    if (Number(targetGroupId) !== Number(selectedGroupId)) {
      setSelectedGroupId(targetGroupId);
    }

    setActiveModal(null);
    setMenu('match');
  };

  const openMonitorPage = () => {
    if (!selectedGroupId) {
      alert('กรุณาเลือก Group ก่อนเปิดหน้า Monitor');
      return;
    }

    setActiveModal(null);
    const monitorUrl = `${window.location.origin}/admin_monitor/${selectedGroupId}`;
    const monitorWindow = window.open(monitorUrl, '_blank');

    // หาก Browser บล็อก Pop-up ให้เปิดในหน้าเดิมแทน
    if (monitorWindow) {
      monitorWindow.opener = null;
    } else {
      window.location.assign(monitorUrl);
    }
  };

  const openMatchMonitorPage = () => {
    if (!selectedGroupId) {
      alert('กรุณาเลือก Group ก่อนเปิดหน้า Match Monitor');
      return;
    }

    setActiveModal(null);
    const monitorUrl = `${window.location.origin}/monitor`;
    const monitorWindow = window.open(monitorUrl, '_blank');

    if (monitorWindow) {
      monitorWindow.opener = null;
    } else {
      window.location.assign(monitorUrl);
    }
  };

  useEffect(() => {
    const action = dashboardAction;
    const requestedGroupId = Number(dashboardGroupId) || null;

    if (!action) return;

    if (requestedGroupId) {
      setSelectedGroupId(requestedGroupId);
    }

    if (action === 'match') {
      setActiveModal(null);
      setMenu('match');
    } else if (MENU_MODAL_DETAILS[action]) {
      if (action === 'court' && requestedGroupId) {
        fetchCourts(requestedGroupId);
      }

      setActiveModal(action);
    }

    navigate('/dashboard', { replace: true, state: null });
  }, [dashboardAction, dashboardGroupId, navigate]);

  const closeMenuModal = () => {
    const closingModal = activeModal;
    setActiveModal(null);

    if (closingModal === 'court' && selectedGroupId) {
      fetchCourts(selectedGroupId);
    }

  };

  const renderGroupsPanel = () => (
    <>
      <div className="card">
        <h3>สร้าง Group</h3>
        <form onSubmit={handleSubmit} className="form-stack">
          <input
            placeholder="Group Name"
            value={groupName}
            onChange={event => setGroupName(event.target.value)}
            required
          />
          <input
            type="date"
            value={groupDate}
            onChange={event => setGroupDate(event.target.value)}
          />
          <button className="primary-btn">สร้าง Group</button>
        </form>
      </div>

      <div className="card">
        <h3>รายการ Group</h3>

        {groups.map(group => (
          <div key={group.id} className="group-card">
            <div>
              <b>{group.Group_Name}</b>
              <div className="sub">
                <span
                  className={`group-status ${group.Group_Status || 'OPEN'}`}
                >
                  {group.Group_Status || 'OPEN'}
                </span>
                <span>📅 {formatGroupDate(group.Group_Date)}</span>
                <span className="group-total-members" title="สมาชิกทุกสถานะใน Group">
                  👥 ทั้งหมด {Number(group.total_members || 0)} คน
                </span>
              </div>
            </div>

            <div className="group-actions">
              <button
                type="button"
                className="group-monitor-qr-btn"
                title={`สร้าง QR ดู Monitor สำหรับ ${group.Group_Name}`}
                aria-label={`สร้าง QR ดู Monitor สำหรับ ${group.Group_Name}`}
                onClick={() => setMonitorQrGroup(group)}
              >
                📺
              </button>
              <button
                type="button"
                className="group-qr-btn"
                title={`สร้าง QR Check-in สำหรับ ${group.Group_Name}`}
                aria-label={`สร้าง QR Check-in สำหรับ ${group.Group_Name}`}
                disabled={['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase())}
                onClick={() => setQrGroup(group)}
              >
                ▦
              </button>
              <button
                type="button"
                className="group-checkin-link-btn"
                title={`เปิดหน้าสมัครสมาชิกสำหรับ ${group.Group_Name}`}
                aria-label={`เปิดหน้าสมัครสมาชิกสำหรับ ${group.Group_Name}`}
                disabled={['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase())}
                onClick={() => openGroupRegisterPage(group)}
              >
                🔗
              </button>
              <button
                type="button"
                title={`${['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? 'เปิด' : 'ปิด'} Group ${group.Group_Name}`}
                aria-label={`${['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? 'เปิด' : 'ปิด'} Group ${group.Group_Name}`}
                disabled={Number(updatingGroupStatusId) === Number(group.id)}
                onClick={() => handleToggleGroupStatus(group)}
                style={{
                  color: ['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? '#047857' : '#b45309',
                  background: ['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? '#d1fae5' : '#fef3c7',
                  border: `1px solid ${['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? '#6ee7b7' : '#fcd34d'}`,
                  opacity: Number(updatingGroupStatusId) === Number(group.id) ? 0.6 : 1,
                  cursor: Number(updatingGroupStatusId) === Number(group.id) ? 'wait' : 'pointer'
                }}
              >
                {Number(updatingGroupStatusId) === Number(group.id)
                  ? '⏳'
                  : ['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? '🔓' : '🔒'}
              </button>

              <button
                type="button"
                title={`จัดการสมาชิก Group ${group.Group_Name}`}
                aria-label={`จัดการสมาชิก Group ${group.Group_Name}`}
                onClick={() => openMenuModal('members', group.id)}
              >
                👥
              </button>

              <button
                type="button"
                title={`จัด Match Group ${group.Group_Name}`}
                aria-label={`จัด Match Group ${group.Group_Name}`}
                onClick={() => openMatchPage(group.id)}
              >
                🎲
              </button>

              <button
                type="button"
                title={`เปิดหน้าจัด Match แบบ Waiting Pair ของ ${group.Group_Name}`}
                aria-label={`เปิด Match Planner Group ${group.Group_Name}`}
                onClick={() => window.open(`/match-planner/${group.id}`, '_blank', 'noopener,noreferrer')}
                style={{ color: '#047857', background: '#d1fae5', border: '1px solid #6ee7b7' }}
              >
                ✨
              </button>

              <button
                type="button"
                title={`จัดการ Court ของ Group ${group.Group_Name}`}
                aria-label={`จัดการ Court ของ Group ${group.Group_Name}`}
                onClick={() => openMenuModal('court', group.id)}
              >
                🏟
              </button>

              <button
                type="button"
                title={`จัดการ Match End ของ Group ${group.Group_Name}`}
                aria-label={`จัดการ Match End ของ Group ${group.Group_Name}`}
                onClick={() => navigate(`/admin/group/${group.id}/matches`)}
              >
                🛡️
              </button>

              <button
                type="button"
                className="delete-group-btn"
                title={`ลบ Group ${group.Group_Name}`}
                aria-label={`ลบ Group ${group.Group_Name}`}
                disabled={Number(deletingGroupId) === Number(group.id)}
                onClick={() => handleDeleteGroup(group)}
                style={{
                  color: '#dc2626',
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  opacity:
                    Number(deletingGroupId) === Number(group.id) ? 0.6 : 1,
                  cursor:
                    Number(deletingGroupId) === Number(group.id)
                      ? 'wait'
                      : 'pointer'
                }}
              >
                {Number(deletingGroupId) === Number(group.id) ? '⏳' : '🗑️'}
              </button>
            </div>
          </div>
        ))}
      </div>
      {qrGroup && (
        <GroupJoinQrModal
          group={qrGroup}
          onClose={() => setQrGroup(null)}
        />
      )}
      {monitorQrGroup && (
        <GroupJoinQrModal
          group={monitorQrGroup}
          mode="monitor"
          onClose={() => setMonitorQrGroup(null)}
        />
      )}
    </>
  );

  const selectedGroup = groups.find(
    group => Number(group.id) === Number(selectedGroupId)
  );
  const plannerShortcutGroup = selectedGroup || groups.find(
    group => String(group.Group_Status || '').toUpperCase() === 'OPEN'
  );
  const activeModalDetails = activeModal
    ? MENU_MODAL_DETAILS[activeModal]
    : null;

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="dash-container">

      <div
        className="dash-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12
        }}
      >
        <span>🏸 Admin Dashboard</span>
        <div className="dash-header-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span className="dash-refresh-countdown" aria-live="polite">
            ⏱ รีเฟรชใน {formatAutoRefreshCountdown(autoRefreshSeconds)}
          </span>
          <button
            type="button"
            onClick={() => navigate('/admin/users')}
            style={{
              padding: '7px 12px', border: '1px solid #c4b5fd',
              borderRadius: 10, color: '#6d28d9', background: '#f5f3ff',
              fontWeight: 800, cursor: 'pointer'
            }}
          >
            🛡️ Admin ผู้เล่นทั้งหมด
          </button>
          <button
            type="button"
            onClick={() => navigate('/rewards')}
            style={{
              padding: '7px 12px', border: '1px solid #f9a8d4',
              borderRadius: 10, color: '#be185d', background: '#fdf2f8',
              fontWeight: 800, cursor: 'pointer'
            }}
          >
            🎁 Admin Reward
          </button>
          <button
            type="button"
            disabled={!plannerShortcutGroup}
            onClick={() => plannerShortcutGroup && window.open(
              `/match-planner/${plannerShortcutGroup.id}`,
              '_blank',
              'noopener,noreferrer'
            )}
            title={plannerShortcutGroup
              ? `เปิด Match Planner: ${plannerShortcutGroup.Group_Name}`
              : 'ยังไม่มี Group เปิดอยู่'}
            style={{
              padding: '7px 12px', border: '1px solid #6ee7b7',
              borderRadius: 10, color: '#047857', background: '#ecfdf5',
              fontWeight: 800,
              cursor: plannerShortcutGroup ? 'pointer' : 'not-allowed',
              opacity: plannerShortcutGroup ? 1 : 0.55
            }}
          >
            ✨ Match Planner
          </button>
          <button
            type="button"
            onClick={refreshDashboard}
            disabled={refreshingDashboard}
            aria-label="รีเฟรชข้อมูล Dashboard"
            style={{
              padding: '7px 12px', border: '1px solid #93c5fd',
              borderRadius: 10, color: '#1d4ed8', background: '#eff6ff',
              fontWeight: 800, cursor: refreshingDashboard ? 'wait' : 'pointer',
              opacity: refreshingDashboard ? 0.65 : 1
            }}
          >
            {refreshingDashboard ? '⏳ กำลังรีเฟรช...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      <div className="dash-content">

        {menu === 'groups' && renderGroupsPanel()}

        {menu === 'members' && selectedGroupId && (
          <GroupMembers key={`members-${selectedGroupId}-${dashboardRefreshVersion}`} groupId={selectedGroupId} />
        )}

        {menu === 'match' && selectedGroupId && (
          <GameMatch key={`match-${selectedGroupId}-${dashboardRefreshVersion}`} groupId={selectedGroupId} />
        )}

        {menu === 'court' && selectedGroupId && (
          <>
            <AddCourt key={`add-court-${selectedGroupId}-${dashboardRefreshVersion}`} groupId={selectedGroupId} />
            <CourtManager
              key={`court-${selectedGroupId}-${dashboardRefreshVersion}`}
              groupId={selectedGroupId}
              courts={courts}   // 🔥 ส่ง realtime ลงไป
            />
          </>
        )}

        {menu === 'players' && (
          <PlayerManager key={`players-${dashboardRefreshVersion}`} />
        )}

      </div>

      <div className="bottom-nav-dash">
        <button
          type="button"
          className={activeModal === 'groups' ? 'is-active' : ''}
          title="เปิดเมนู Groups"
          aria-label="เปิดหน้าต่างจัดการ Groups"
          aria-haspopup="dialog"
          aria-expanded={activeModal === 'groups'}
          onClick={() => openMenuModal('groups')}
        >
          📁
        </button>
        <button
          type="button"
          className={activeModal === 'members' ? 'is-active' : ''}
          title="เปิดเมนูสมาชิก"
          aria-label="เปิดหน้าต่างจัดการสมาชิก"
          aria-haspopup="dialog"
          aria-expanded={activeModal === 'members'}
          onClick={() => openMenuModal('members')}
        >
          👥
        </button>
        <button
          type="button"
          className={!activeModal && menu === 'match' ? 'is-active' : ''}
          title="กลับหน้าจัด Match"
          aria-label="กลับหน้าจัด Match"
          onClick={() => openMatchPage()}
        >
          🎲
        </button>
        <button
          type="button"
          className={activeModal === 'court' ? 'is-active' : ''}
          title="เปิดเมนู Court"
          aria-label="เปิดหน้าต่างจัดการ Court"
          aria-haspopup="dialog"
          aria-expanded={activeModal === 'court'}
          onClick={() => openMenuModal('court')}
        >
          🏟
        </button>
        <button
          type="button"
          className={activeModal === 'players' ? 'is-active' : ''}
          title="เปิดเมนูผู้เล่น"
          aria-label="เปิดหน้าต่างจัดการผู้เล่น"
          aria-haspopup="dialog"
          aria-expanded={activeModal === 'players'}
          onClick={() => openMenuModal('players')}
        >
          🧑
        </button>
        <button
          type="button"
          title="เปิดหน้า Admin Monitor ในแท็บใหม่"
          aria-label="เปิดหน้า Admin Monitor ในแท็บใหม่"
          onClick={openMonitorPage}
        >
          📺
        </button>
        <button
          type="button"
          title="เปิดหน้า Match Monitor ในแท็บใหม่"
          aria-label="เปิดหน้า Match Monitor ในแท็บใหม่"
          onClick={openMatchMonitorPage}
        >
          🖥️
        </button>
        <button type="button" title="ออกจากระบบ" onClick={logout}>🚪</button>
      </div>

      {activeModal && activeModalDetails && createPortal(
        <div
          className="court-manager-modal-backdrop"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) {
              closeMenuModal();
            }
          }}
        >
          <section
            className={`court-manager-modal dashboard-menu-modal is-${activeModal}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dashboard-menu-modal-title"
          >
            <header className="court-manager-modal-header">
              <div>
                <span className="court-manager-modal-eyebrow">
                  {activeModalDetails.eyebrow}
                </span>
                <h2 id="dashboard-menu-modal-title">
                  {activeModalDetails.title}
                </h2>
                <p>
                  {activeModalDetails.description}
                  {['members', 'court'].includes(activeModal) &&
                    selectedGroup?.Group_Name &&
                    ` • Group: ${selectedGroup.Group_Name}`}
                </p>
              </div>

              <button
                type="button"
                className="court-manager-modal-close-icon"
                aria-label={`ปิด${activeModalDetails.title}`}
                title="ปิด"
                onClick={closeMenuModal}
              >
                ✕
              </button>
            </header>

            <div className="court-manager-modal-body dashboard-menu-modal-body">
              {activeModal === 'groups' && (
                <div className="dashboard-menu-modal-stack">
                  {renderGroupsPanel()}
                </div>
              )}

              {activeModal === 'members' && selectedGroupId && (
                <GroupMembers groupId={selectedGroupId} />
              )}

              {activeModal === 'court' && selectedGroupId && (
                <>
                  <AddCourt
                    groupId={selectedGroupId}
                    onChanged={() => fetchCourts(selectedGroupId)}
                  />
                  <CourtManager
                    groupId={selectedGroupId}
                    courts={courts}
                  />
                </>
              )}

              {activeModal === 'players' && <PlayerManager />}
            </div>

            <footer className="court-manager-modal-footer">
              <span>ข้อมูลจะอัปเดตโดยไม่เปลี่ยนหน้าหลัก</span>
              <button
                type="button"
                className="court-manager-modal-done"
                onClick={closeMenuModal}
              >
                ✓ จัดการเสร็จแล้ว — ปิด
              </button>
            </footer>
          </section>
        </div>,
        document.body
      )}

    </div>
  );
}

export default Dashboard;
